import { Suspense, lazy } from "react"
import { Navigate, useParams, type RouteObject } from "react-router"

import { LoadingFallback } from "./pages/_loading"
import { RouteError } from "./pages/_error"
import { LibraryLayout } from "./pages/library/_layout"
import ProductDashboard from "./pages/_product-dashboard"
import { ProductRootGate } from "@/contexts/product-root-gate"
import { RedirectPrismExamManagement } from "./pages/_redirect-prism-em"
import { ExamManagementLanding } from "./pages/_exam-management-landing"

/**
 * Route table — Exam Management owns assessment surfaces under
 * `/:productRootSegment/` (e.g. `/exam-management/course-offerings`).
 * Clinical Education (Prism) keeps placements under `/prism/...`.
 * Root `/` redirects into Exam Management.
 */

function lazyPage(
  loader: () => Promise<{ default: React.ComponentType }>,
  fallback: React.ReactNode = <LoadingFallback />,
) {
  const Lazy = lazy(loader)
  return (
    <Suspense fallback={fallback}>
      <Lazy />
    </Suspense>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Exam Management entity routes (flat under product root)
// ─────────────────────────────────────────────────────────────────────────────

function libraryChildren(): RouteObject[] {
  return [
    { index: true, element: lazyPage(() => import("./pages/library/index")) },
    { path: "all", element: lazyPage(() => import("./pages/library/all")) },
    { path: "find", element: lazyPage(() => import("./pages/library/find")) },
    { path: "list", element: lazyPage(() => import("./pages/library/list")) },
    { path: "new", element: lazyPage(() => import("./pages/library/new")) },
  ]
}

/**
 * Exam Management is the one product root with no dashboard of its own, so it
 * lands on the course list instead. Every other root — Design OS above all —
 * has one, and hard-redirecting them into Exam Management stranded their nav.
 */
function ProductRootDashboard() {
  const { productRootSegment } = useParams()
  if (productRootSegment === "exam-management") {
    return <ExamManagementLanding />
  }
  return <ProductDashboard />
}

function examManagementChildren(): RouteObject[] {
  return [
    { index: true, element: <ExamManagementLanding /> },
    // Shell breadcrumbs and the product switcher both resolve to
    // `<root>/dashboard`, so the segment has to exist for every product.
    {
      path: "dashboard",
      element: <ProductRootDashboard />,
      errorElement: <RouteError />,
    },
    {
      path: "course-offerings",
      element: lazyPage(() => import("./pages/course-offerings")),
      errorElement: <RouteError />,
    },
    {
      path: "course-offerings/:courseId",
      element: lazyPage(() => import("./pages/course-offering-details")),
      errorElement: <RouteError />,
    },
    {
      path: "course-offerings/:courseId/assessments/:assessmentId/sections/:sectionId/add-questions",
      element: lazyPage(() => import("./pages/assessment-add-questions")),
      errorElement: <RouteError />,
    },
    {
      path: "course-offerings/:courseId/assessments/:assessmentId/questions/new",
      element: lazyPage(() => import("./pages/assessment-question-create")),
      errorElement: <RouteError />,
    },
    {
      path: "course-offerings/:courseId/assessments/:assessmentId/adjust-key",
      element: lazyPage(() => import("./pages/assessment-adjust-key")),
      errorElement: <RouteError />,
    },
    {
      path: "course-offerings/:courseId/assessments/:assessmentId/preview",
      element: lazyPage(() => import("./pages/assessment-preview")),
      errorElement: <RouteError />,
    },
    {
      path: "course-offerings/:courseId/assessments/:assessmentId",
      element: lazyPage(() => import("./pages/assessment-details")),
      errorElement: <RouteError />,
    },
    {
      path: "assessments/:assessmentId/sections/:sectionId/add-questions",
      element: lazyPage(() => import("./pages/assessment-add-questions")),
      errorElement: <RouteError />,
    },
    {
      path: "assessments/:assessmentId/questions/new",
      element: lazyPage(() => import("./pages/assessment-question-create")),
      errorElement: <RouteError />,
    },
    {
      path: "assessments/:assessmentId/adjust-key",
      element: lazyPage(() => import("./pages/assessment-adjust-key")),
      errorElement: <RouteError />,
    },
    {
      path: "assessments/:assessmentId",
      element: lazyPage(() => import("./pages/assessment-details")),
      errorElement: <RouteError />,
    },
    {
      path: "assessments/:assessmentId/preview",
      element: lazyPage(() => import("./pages/assessment-preview")),
      errorElement: <RouteError />,
    },
    {
      path: "assessments",
      element: lazyPage(() => import("./pages/assessments")),
      errorElement: <RouteError />,
    },
    {
      path: "assessments/create",
      element: <Navigate to="/exam-management/assessments" replace />,
      errorElement: <RouteError />,
    },
    {
      path: "submissions",
      element: lazyPage(() => import("./pages/submissions")),
      errorElement: <RouteError />,
    },
    {
      path: "submissions/:submissionId",
      element: lazyPage(() => import("./pages/submission-details")),
      errorElement: <RouteError />,
    },
    {
      path: "question-bank",
      element: lazyPage(() => import("./pages/question-bank")),
      errorElement: <RouteError />,
    },
    {
      path: "question-bank/create",
      element: lazyPage(() => import("./pages/question-bank-create")),
      errorElement: <RouteError />,
    },
    {
      path: "question-bank/import/:jobId",
      element: lazyPage(() => import("./pages/question-bank-import-review")),
      errorElement: <RouteError />,
    },
    {
      // Legacy path, kept so old links resolve to whatever is actually waiting.
      path: "question-bank/import-review",
      element: lazyPage(() => import("./pages/question-bank-import-review")),
      errorElement: <RouteError />,
    },
    {
      path: "students/roster",
      element: lazyPage(() => import("./pages/students-roster")),
      errorElement: <RouteError />,
    },
    {
      path: "my-exams",
      element: lazyPage(() => import("./pages/my-exams")),
      errorElement: <RouteError />,
    },
    {
      path: "my-exams/:assessmentId/results",
      element: lazyPage(() => import("./pages/student-exam-results")),
      errorElement: <RouteError />,
    },
    {
      path: "results",
      element: lazyPage(() => import("./pages/my-results")),
      errorElement: <RouteError />,
    },
    {
      path: "results/:assessmentId",
      element: lazyPage(() => import("./pages/student-result-detail")),
      errorElement: <RouteError />,
    },
    {
      path: "results/:assessmentId/review",
      element: lazyPage(
        () => import("./pages/student-exam-results-legacy-redirect"),
      ),
      errorElement: <RouteError />,
    },
    {
      path: "students",
      element: lazyPage(() => import("./pages/students")),
      errorElement: <RouteError />,
    },
    {
      path: "my-dashboard",
      element: lazyPage(() => import("./pages/my-dashboard")),
      errorElement: <RouteError />,
    },
    {
      path: "students/:studentId",
      element: lazyPage(() => import("./pages/student-details")),
      errorElement: <RouteError />,
    },
    {
      path: "faculty",
      element: lazyPage(() => import("./pages/faculty")),
      errorElement: <RouteError />,
    },
    {
      path: "faculty/:facultyId",
      element: lazyPage(() => import("./pages/faculty-details")),
      errorElement: <RouteError />,
    },
    {
      path: "reports",
      element: lazyPage(() => import("./pages/reports")),
      errorElement: <RouteError />,
    },
    {
      path: "project-roadmap",
      element: lazyPage(() => import("./pages/project-roadmap")),
      errorElement: <RouteError />,
    },
    {
      path: "categories",
      element: lazyPage(() => import("./pages/categories")),
      errorElement: <RouteError />,
    },
    {
      path: "settings",
      element: lazyPage(() => import("./pages/settings-hub")),
      errorElement: <RouteError />,
    },
    // Design OS — the catalog that ships with @exxatdesignux/ui. Its sidebar
    // (`buildDesignOsNavLayout`) points at library, tokens, and columns, so all
    // three have to be routable or the product opens onto dead links.
    {
      path: "library",
      element: <LibraryLayout />,
      children: libraryChildren(),
      errorElement: <RouteError />,
    },
    {
      path: "design-system",
      element: lazyPage(() => import("./pages/design-system/_layout")),
      errorElement: <RouteError />,
      children: [
        { index: true, element: lazyPage(() => import("./pages/design-system/index")) },
        { path: ":slug", element: lazyPage(() => import("./pages/design-system/doc")) },
      ],
    },
    {
      path: "tokens-themes",
      element: lazyPage(() => import("./pages/tokens-themes")),
      errorElement: <RouteError />,
    },
    {
      path: "columns",
      element: lazyPage(() => import("./pages/columns")),
      errorElement: <RouteError />,
    },
    {
      path: "column-types-demo",
      element: lazyPage(() => import("./pages/column-types-demo")),
      errorElement: <RouteError />,
    },
    {
      path: "leo",
      element: lazyPage(() => import("./pages/leo")),
      errorElement: <RouteError />,
    },
    {
      path: "focus-workflow",
      element: lazyPage(() => import("./pages/focus-workflow")),
      errorElement: <RouteError />,
    },
    {
      path: "exam-lock",
      element: lazyPage(() => import("./pages/exam-lock")),
      errorElement: <RouteError />,
    },
    {
      path: "settings/access-controls",
      element: <Navigate to="/exam-management/settings" replace />,
      errorElement: <RouteError />,
    },
    {
      path: "locations",
      element: lazyPage(() => import("./pages/one/locations")),
      errorElement: <RouteError />,
    },
    {
      path: "slot-requests",
      element: lazyPage(() => import("./pages/one/slot-requests")),
      errorElement: <RouteError />,
    },
  ]
}

export const routes: RouteObject[] = [
  // Root → Exam Management
  { index: true, element: <Navigate to="/exam-management/course-offerings/c1" replace /> },

  // Back-compat: old dashboard URL
  { path: "dashboard", element: <Navigate to="/exam-management/course-offerings/c1" replace /> },

  // Back-compat: EM surfaces that were incorrectly rooted under Prism
  {
    path: "prism/course-offerings",
    element: <RedirectPrismExamManagement segment="course-offerings" />,
  },
  {
    path: "prism/course-offerings/*",
    element: <RedirectPrismExamManagement segment="course-offerings" />,
  },
  {
    path: "prism/assessments",
    element: <RedirectPrismExamManagement segment="assessments" />,
  },
  {
    path: "prism/assessments/*",
    element: <RedirectPrismExamManagement segment="assessments" />,
  },
  {
    path: "prism/question-bank",
    element: <RedirectPrismExamManagement segment="question-bank" />,
  },
  {
    path: "prism/question-bank/*",
    element: <RedirectPrismExamManagement segment="question-bank" />,
  },
  {
    path: "prism/submissions",
    element: <RedirectPrismExamManagement segment="submissions" />,
  },
  {
    path: "prism/submissions/*",
    element: <RedirectPrismExamManagement segment="submissions" />,
  },

  // Per-user settings are a shell route, not a product one: the profile page
  // is the same page whichever product the author came from, so it has no
  // product root to sit under (`exxat-product-routing`, "when this rule does
  // not apply"). The pages already existed and were linked from the user menu
  // and the command palette; only the route table was missing them.
  {
    path: "home",
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        element: lazyPage(() => import("./pages/product-home-index")),
      },
      { path: "storefront", element: lazyPage(() => import("./pages/product-home")) },
      { path: "focus", element: lazyPage(() => import("./pages/product-home")) },
      { path: "spotlight", element: lazyPage(() => import("./pages/product-home")) },
      { path: "launcher", element: lazyPage(() => import("./pages/product-home")) },
      {
        path: ":productSlug",
        element: lazyPage(() => import("./pages/product-marketing")),
      },
    ],
  },
  {
    path: "builder/onboarding",
    element: lazyPage(() => import("./pages/builder-onboarding")),
    errorElement: <RouteError />,
  },
  {
    path: "settings",
    element: lazyPage(() => import("./pages/settings-layout")),
    errorElement: <RouteError />,
    children: [
      { index: true, element: lazyPage(() => import("./pages/settings-legacy-redirect")) },
      { path: "profile", element: lazyPage(() => import("./pages/settings-profile")) },
      {
        path: "organization",
        element: lazyPage(() => import("./pages/settings-organization")),
      },
    ],
  },

  // Product-owned root
  {
    path: ":productRootSegment",
    element: <ProductRootGate />,
    children: examManagementChildren(),
    errorElement: <RouteError />,
  },

  // Catch-all 404
  {
    path: "*",
    element: lazyPage(() => import("./pages/_not-found")),
  },
]
